<template>
  <div class="video-list-page">
    <!-- Main 内容区 -->
    <main class="main-content">
      <div class="container">
        <!-- 页面标题 -->
        <div class="page-header">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-desc">{{ pageDesc }}</p>
        </div>

        <!-- 筛选栏 -->
        <div class="filter-bar">
          <div class="filter-item">
            <label>分类：</label>
            <el-select v-model="categoryId" placeholder="全部分类" clearable @change="loadVideoList" size="small">
              <el-option label="全部分类" value=""></el-option>
              <el-option
                v-for="item in categoryList"
                :key="item.id"
                :label="item.name"
                :value="item.id">
              </el-option>
            </el-select>
          </div>
        </div>

        <!-- 视频列表 -->
        <div class="video-grid" v-loading="loading">
          <div
            v-for="video in videoList"
            :key="video.videoId"
            class="video-card"
            @click="goToDetail(video.videoId)"
          >
            <div class="video-thumbnail">
              <div class="thumbnail-bg" :style="{ backgroundImage: 'url(' + video.thumbnail + ')' }"></div>
              <div class="video-watch-status">
                <WatchStatusBadge :status="video.watchStatus" />
              </div>
              <div class="video-duration">{{ video.duration }}</div>
              <div class="video-overlay">
                <PlayCircle class="play-icon" />
              </div>
            </div>
            <div class="video-info">
              <span :class="['video-category', video.categoryClass]">{{ video.category }}</span>
              <h3 class="video-title">{{ video.title }}</h3>
              <div class="video-meta">
                <span class="video-views">
                  <Eye class="icon-sm" />
                  {{ video.views }} 次观看
                </span>
                <span class="video-time">{{ video.timeAgo }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && videoList.length === 0" class="empty-state">
          <Inbox class="empty-icon" />
          <p>暂无视频内容</p>
        </div>

        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="total > 0">
          <el-pagination
            background
            layout="total, prev, pager, next"
            :page-size="pageSize"
            :current-page="pageNumber"
            :total="total"
            @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-left">
            <PlayCircle class="footer-icon" />
            <span class="footer-text">© 2024 智学云平台。保留所有权利。</span>
          </div>
          <div class="footer-links">
            <a href="#">帮助中心</a>
            <a href="#">服务条款</a>
            <a href="#">隐私政策</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { getVideoPage, getApeClassificationList } from '../../api/api'
import { consumePageState, restoreWindowScroll, savePageState } from '../../utils/pageState'
import { DEFAULT_VIDEO_PLACEHOLDER } from '../../utils/placeholders'
import WatchStatusBadge from '../../components/video/WatchStatusBadge.vue'

export default {
  name: 'VideoList',
  components: {
    WatchStatusBadge
  },
  data() {
    return {
      loading: false,
      searchKey: '',
      videoList: [],
      categoryList: [],
      categoryId: '',
      pageNumber: 1,
      pageSize: 12,
      pendingScrollTop: null,
      total: 0,
      pageType: '' // latest, popular, search
    }
  },
  computed: {
    pageTitle() {
      if (this.pageType === 'latest') return '最新视频'
      if (this.pageType === 'popular') return '热门推荐'
      if (this.$route.query.keyword) return '搜索结果'
      return '视频列表'
    },
    pageDesc() {
      if (this.pageType === 'latest') return '来自组织和单位的最新上传内容'
      if (this.pageType === 'popular') return '最受欢迎的视频内容'
      if (this.$route.query.keyword) return `搜索"${this.$route.query.keyword}"的相关内容`
      return '浏览所有视频内容'
    }
  },
  watch: {
    '$route'(to, from) {
      this.initPage()
    }
  },
  methods: {
    getPageStateKey() {
      return `video-list:${this.$route.query.type || ''}:${this.$route.query.keyword || ''}`
    },
    restorePageState() {
      const savedState = consumePageState(this.getPageStateKey())
      if (!savedState) {
        return
      }

      this.categoryId = savedState.categoryId || ''
      this.pageNumber = savedState.pageNumber || 1
      this.pendingScrollTop = Number.isFinite(savedState.scrollTop) ? savedState.scrollTop : 0
    },
    saveCurrentPageState() {
      savePageState(this.getPageStateKey(), {
        categoryId: this.categoryId,
        pageNumber: this.pageNumber,
        scrollTop: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      })
    },
    restoreSavedScroll() {
      if (this.pendingScrollTop === null) {
        return
      }

      restoreWindowScroll(this.pendingScrollTop)
      this.pendingScrollTop = null
    },
    initPage() {
      this.pageType = this.$route.query.type || ''
      this.searchKey = this.$route.query.keyword || ''
      this.categoryId = ''
      this.pageNumber = 1
      this.restorePageState()
      this.loadVideoList()
      this.loadCategoryList()
    },
    async loadVideoList() {
      this.loading = true
      try {
        const params = {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          videoTitle: this.searchKey,
          categoryId: this.categoryId,
          visibility: '0' // 只查询公区视频
        }

        const res = await getVideoPage(params)
        if (res.code === 1000 && res.data) {
          this.videoList = res.data.records.map(video => this.formatVideoData(video))
          this.total = res.data.total
        }
      } catch (error) {
        console.error('加载视频列表失败:', error)
        this.$message.error('加载视频列表失败')
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.restoreSavedScroll()
        })
      }
    },
    async loadCategoryList() {
      try {
        const res = await getApeClassificationList()
        if (res.code === 1000) {
          this.categoryList = res.data
        }
      } catch (error) {
        console.error('加载分类列表失败:', error)
      }
    },
    formatVideoData(video) {
      return {
        videoId: video.videoId,
        thumbnail: this.getImageUrl(video.videoCover),
        duration: this.formatDuration(video.duration),
        category: video.categoryName ? video.categoryName.split(',')[0] : '未分类',
        categoryClass: this.getCategoryClass(video.categoryName),
        title: video.videoTitle,
        views: this.formatNumber(video.viewCount || 0),
        timeAgo: this.formatTimeAgo(video.createTime),
        watchStatus: video.watchStatus || '待观看'
      }
    },
    getImageUrl(path) {
      if (!path) return DEFAULT_VIDEO_PLACEHOLDER
      if (path.startsWith('http')) return path
      return this.$store.state.HOST + path
    },
    formatDuration(seconds) {
      if (!seconds) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k'
      }
      return num.toString()
    },
    formatTimeAgo(dateString) {
      if (!dateString) return '刚刚'
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      const months = Math.floor(days / 30)

      if (months > 0) return months + ' 个月前'
      if (days > 0) return days + ' 天前'
      if (hours > 0) return hours + ' 小时前'
      if (minutes > 0) return minutes + ' 分钟前'
      return '刚刚'
    },
    getCategoryClass(categoryName) {
      const classes = ['category-blue', 'category-red', 'category-purple', 'category-green', 'category-orange', 'category-indigo']
      if (!categoryName) return classes[0]
      const hash = categoryName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      return classes[hash % classes.length]
    },
    handleSearch() {
      if (this.searchKey) {
        this.$router.push({
          path: '/videoList',
          query: { keyword: this.searchKey }
        })
      }
    },
    handlePageChange(page) {
      this.pageNumber = page
      this.loadVideoList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goToDetail(videoId) {
      if (!videoId) return
      this.saveCurrentPageState()
      this.$router.push({ path: '/videoPlay', query: { id: videoId, from: 'public' } })
    }
  },
  mounted() {
    this.initPage()
  }
}
</script>

<style scoped>
.video-list-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', 'Helvetica Neue', Arial, sans-serif;
  color: #101318;
  background-color: #f0f2f4;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header 样式 */
.header {
  position: sticky;
  top: 0;
  z-index: 50;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 2.5rem;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1a71ff;
  cursor: pointer;
}

.logo-icon {
  font-size: 32px;
}

.brand-name {
  color: #101318;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-link {
  text-decoration: none;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-link:hover,
.nav-link.active {
  color: #1a71ff;
}

.header-right {
  display: flex;
  align-items: center;
}

.search-box {
  position: relative;
  width: 320px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 40px;
  border: none;
  border-radius: 9999px;
  background-color: #f0f2f5;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  background-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(26, 113, 255, 0.2);
}

/* Main Content */
.main-content {
  flex: 1;
  padding: 2rem 0;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
}

.page-desc {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #ffffff;
  border-radius: 8px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
}

/* Video Grid */
.video-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

@media (max-width: 1024px) {
  .video-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .video-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .video-grid {
    grid-template-columns: 1fr;
  }
}

.video-card {
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  cursor: pointer;
}

.video-card:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: #e5e7eb;
}

.thumbnail-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s;
}

.video-card:hover .thumbnail-bg {
  transform: scale(1.1);
}

.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 2px;
}

.video-watch-status {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.video-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s;
}

.video-card:hover .video-overlay {
  opacity: 1;
}

.play-icon {
  color: #ffffff;
  width: 48px;
  height: 48px;
}

.icon-sm {
  width: 16px;
  height: 16px;
}

.video-info {
  padding: 1rem;
}

.video-category {
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 2px;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 8px;
}

.category-blue {
  background-color: #dbeafe;
  color: #1e40af;
}

.category-red {
  background-color: #fee2e2;
  color: #b91c1c;
}

.category-purple {
  background-color: #ede9fe;
  color: #6b21a8;
}

.category-green {
  background-color: #d1fae5;
  color: #065f46;
}

.category-orange {
  background-color: #fed7aa;
  color: #c2410c;
}

.category-indigo {
  background-color: #e0e7ff;
  color: #3730a3;
}

.video-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
}

.video-views {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 0;
}

.empty-icon {
  width: 64px;
  height: 64px;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.empty-state p {
  font-size: 16px;
  color: #6b7280;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

/* Footer */
.footer {
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  margin-top: 3rem;
}

.footer .container {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.footer-icon {
  color: #9ca3af;
  width: 20px;
  height: 20px;
}

.footer-text {
  font-size: 14px;
  color: #6b7280;
}

.footer-links {
  display: flex;
  gap: 1.5rem;
}

.footer-links a {
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: #1a71ff;
}
</style>
